import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Loader2, ChevronRight, Network } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrgStructure = ({ project, landscape }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('diagram'); // diagram or grid

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/discovery/org-structure`);
      setData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return <Loader2 className="w-12 h-12 animate-spin mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-6 h-6 mr-3 text-blue-600" />
            Organizational Structure Analysis
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('diagram')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'diagram' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Diagram View
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Grid View
            </button>
          </div>
        </div>

        {landscape && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800">
              <strong>Analyzing:</strong> {landscape.name} - {landscape.systems_detail?.length || 0} systems
            </p>
          </div>
        )}
      </div>

      {/* Diagram View */}
      {viewMode === 'diagram' && (
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <h4 className="text-xl font-bold text-gray-900 mb-8 text-center">SAP Organizational Hierarchy</h4>
          
          {/* Visual Diagram */}
          <div className="space-y-12">
            {/* Financial */}
            <div className="border-2 border-blue-200 rounded-2xl p-6 bg-gradient-to-r from-blue-50 to-cyan-50">
              <div className="text-center mb-6">
                <div className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl font-bold text-lg shadow-lg">
                  FI - Financial Accounting
                </div>
              </div>
              <div className="flex justify-center items-start space-x-8">
                <div className="text-center">
                  <div className="w-40 p-4 bg-white border-2 border-blue-300 rounded-xl shadow-md">
                    <p className="font-bold text-gray-900 mb-2">Company Codes</p>
                    <div className="space-y-1">
                      {data.modules.FI.company_codes.map((code) => (
                        <div key={code} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm font-semibold">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 mx-auto my-2 text-blue-600" style={{ transform: 'rotate(90deg)' }} />
                  <div className="w-40 p-3 bg-white border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700">Chart of Accounts</p>
                    <p className="text-xs text-gray-600 mt-1">{data.modules.FI.chart_of_accounts.join(', ')}</p>
                  </div>
                </div>
                
                <ChevronRight className="w-8 h-8 text-blue-400 mt-8" />
                
                <div className="text-center">
                  <div className="w-40 p-4 bg-white border-2 border-blue-300 rounded-xl shadow-md">
                    <p className="font-bold text-gray-900 mb-2">Fiscal Year</p>
                    <div className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-lg font-bold">
                      {data.modules.FI.fiscal_year_variant}
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 mx-auto my-2 text-blue-600" style={{ transform: 'rotate(90deg)' }} />
                  <div className="w-40 p-3 bg-white border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700">Periods</p>
                    <p className="text-lg font-bold text-blue-600">{data.modules.FI.posting_periods}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales & Distribution */}
            <div className="border-2 border-green-200 rounded-2xl p-6 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="text-center mb-6">
                <div className="inline-block px-6 py-3 bg-green-600 text-white rounded-xl font-bold text-lg shadow-lg">
                  SD - Sales & Distribution
                </div>
              </div>
              <div className="flex justify-center items-center space-x-4">
                <div className="w-36 p-4 bg-white border-2 border-green-300 rounded-xl shadow-md text-center">
                  <p className="font-bold text-gray-900 mb-2">Sales Orgs</p>
                  {data.modules.SD.sales_orgs.map((org) => (
                    <div key={org} className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold mb-1">
                      {org}
                    </div>
                  ))}
                </div>
                <ChevronRight className="w-6 h-6 text-green-600" />
                <div className="w-36 p-4 bg-white border-2 border-green-300 rounded-xl shadow-md text-center">
                  <p className="font-bold text-gray-900 mb-2">Channels</p>
                  {data.modules.SD.distribution_channels.map((ch) => (
                    <div key={ch} className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold mb-1">
                      {ch}
                    </div>
                  ))}
                </div>
                <ChevronRight className="w-6 h-6 text-green-600" />
                <div className="w-36 p-4 bg-white border-2 border-green-300 rounded-xl shadow-md text-center">
                  <p className="font-bold text-gray-900 mb-2">Divisions</p>
                  {data.modules.SD.divisions.map((div) => (
                    <div key={div} className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold mb-1">
                      {div}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Materials Management */}
            <div className="border-2 border-purple-200 rounded-2xl p-6 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="text-center mb-6">
                <div className="inline-block px-6 py-3 bg-purple-600 text-white rounded-xl font-bold text-lg shadow-lg">
                  MM - Materials Management
                </div>
              </div>
              <div className="flex justify-center items-center space-x-4">
                <div className="w-44 p-4 bg-white border-2 border-purple-300 rounded-xl shadow-md text-center">
                  <p className="font-bold text-gray-900 mb-2">Plants</p>
                  <div className="grid grid-cols-2 gap-1">
                    {data.modules.MM.plants.map((plant) => (
                      <div key={plant} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-semibold">
                        {plant}
                      </div>
                    ))}
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-purple-600" />
                <div className="w-36 p-4 bg-white border-2 border-purple-300 rounded-xl shadow-md text-center">
                  <p className="font-bold text-gray-900 mb-2">Storage Locations</p>
                  <p className="text-2xl font-bold text-purple-600">{data.modules.MM.storage_locations}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(data.modules).map(([module, details]) => (
            <div key={module} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
              <h4 className="text-lg font-bold text-gray-900 mb-4 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg inline-block">
                {module}
              </h4>
              <div className="space-y-2 text-sm">
                {Object.entries(details).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="font-bold text-gray-900">
                      {Array.isArray(value) ? value.join(', ') : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
